import { useBlocker } from "@tanstack/react-router";
import { Modal } from "antd";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useCallback, useEffect, useRef } from "react";

/**
 * Warns before losing unsaved changes: route change (modal) and page refresh/close (browser prompt).
 * Query-param-only navigation (drawer open/close) is not blocked. Returns `confirmDiscard` for closing a drawer.
 */
export const useUnsavedChangesGuard = (isDirty: boolean) => {
    const { t: tC } = usePaldockTranslation("common");
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    const { proceed, reset, status } = useBlocker({
        shouldBlockFn: ({ current, next }) => current.pathname !== next.pathname && isDirtyRef.current,
        enableBeforeUnload: () => isDirtyRef.current,
        withResolver: true,
    });

    const confirmDiscard = useCallback(
        (): Promise<boolean> =>
            new Promise((resolve) => {
                Modal.confirm({
                    title: tC("unsaved_changes"),
                    content: tC("unsaved_changes_message"),
                    okText: tC("discard"),
                    cancelText: tC("cancel"),
                    okType: "danger",
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                });
            }),
        [tC]
    );

    const modalRef = useRef<ReturnType<typeof Modal.confirm> | null>(null);
    useEffect(() => {
        if (status !== "blocked") {
            return;
        }
        if (!isDirtyRef.current) {
            proceed();
            return;
        }
        modalRef.current?.destroy();
        modalRef.current = Modal.confirm({
            title: tC("unsaved_changes"),
            content: tC("unsaved_changes_message"),
            okText: tC("discard"),
            cancelText: tC("cancel"),
            okType: "danger",
            onOk: () => {
                modalRef.current = null;
                proceed();
            },
            onCancel: () => {
                modalRef.current = null;
                reset();
            },
        });
    }, [status, proceed, reset, tC]);

    useEffect(
        () => () => {
            modalRef.current?.destroy();
            modalRef.current = null;
        },
        []
    );

    return { confirmDiscard };
};
